import {Outlet, useMatch} from '@github-ui/react-router'
import {pullRequestsLayoutRoute} from './layout-route'
import {useRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {useRespondToRouteChange} from '../hooks/use-respond-to-route-change'
import {type PropsWithChildren, useCallback, useRef} from 'react'
import {PageDataContextProvider} from '@github-ui/pull-request-page-data-tooling/page-data-context'
import {DockedPanelProvider} from '../contexts/DockedPanelContext'
import {
  condensedResponsiveWrapperClasses,
  fullWidthResponsiveWrapperClasses,
} from '../components/StickyPullRequestHeader'
import {ChangesRoutePath, ChecksRoutePath, FilesRoutePath} from './route-paths'
import {DiffComparisonViewer} from '../components/DiffComparisonViewer'
import {UserNameDisplayContextProvider} from '@github-ui/profile-reference/UserNameDisplayContext'
import {LivePullRequestHeader} from '../components/PullRequestHeader'
import type {HeaderPageData} from '../page-data/payloads/header'
import {useRespondToAliveUpdate} from '../hooks/use-refetch-on-alive-update'
import {useQueryClient} from '@github-ui/react-query'
import {issueCommentsQueryKey} from '../page-data/loaders/use-issue-comments'
import {threadPreviewsQueryKey} from '../page-data/loaders/use-thread-previews'

export function LayoutEntrypoint() {
  const {
    data: {
      pullRequest,
      bannersData,
      repository,
      urls,
      user,
      aliveChannels,
      pageTitle,
      stack,
      mergeStatusButtonData,
      viewSettings,
    },
  } = useRouteQuery(pullRequestsLayoutRoute, 'mainQuery')
  const {refetch: refetchTabCounts} = useRouteQuery(pullRequestsLayoutRoute, 'tabCounts')
  const {refetch: refetchDiffstat} = useRouteQuery(pullRequestsLayoutRoute, 'diffstat')
  const needUpdateRef = useRef(false)
  const queryClient = useQueryClient()

  const markNeedsUpdate = useCallback(() => {
    needUpdateRef.current = true
  }, [])

  useRespondToAliveUpdate(aliveChannels.pullRequest, markNeedsUpdate, {git_updated: true, timeline_updated: true})

  // Refetch shared data when the route changes to keep diffstat and tab counts up to date
  // we only refetch on navigation to avoid excessive refetching.
  const handleRouteChange = useCallback(() => {
    //TODO: when the pull request store has been elevated to exist at the layout level, we no longer have to invalidate
    // these queries. This code can be removed. In the interim, though, we are invalidating the comment side panel
    // queries to make sure the pull request store is updated with the latest data.

    // we do not have access to the base path URL in the layout, but we can remove the '/changes' from the end of the
    // changes URL to get it.
    const basePathUrl = urls.changes.substring(0, urls.changes.length - 8)
    queryClient.invalidateQueries({queryKey: issueCommentsQueryKey(basePathUrl)})
    queryClient.invalidateQueries({queryKey: threadPreviewsQueryKey(basePathUrl)})
    if (!needUpdateRef.current) return

    needUpdateRef.current = false
    refetchTabCounts()
    refetchDiffstat()
  }, [queryClient, urls.changes, refetchTabCounts, refetchDiffstat])

  useRespondToRouteChange(handleRouteChange)

  return (
    <LayoutComponent
      aliveChannels={aliveChannels}
      pullRequest={pullRequest}
      bannersData={bannersData}
      repository={repository}
      pageTitle={pageTitle}
      urls={urls}
      user={user}
      mergeStatusButtonData={mergeStatusButtonData}
      stack={stack}
      viewSettings={viewSettings}
    >
      <Outlet />
    </LayoutComponent>
  )
}

export function LayoutComponent({
  aliveChannels,
  pullRequest,
  bannersData,
  pageTitle,
  repository,
  stack,
  urls,
  user,
  children,
  mergeStatusButtonData,
  viewSettings,
}: PropsWithChildren<HeaderPageData>) {
  const isFilesRoute = useMatch(`${FilesRoutePath}/*`) !== null
  const isChangesRoute = useMatch(`${ChangesRoutePath}/*`) !== null
  const isChecksRoute = useMatch(`${ChecksRoutePath}/*`) !== null

  const isFilesOrChangesRoute = isFilesRoute || isChangesRoute

  const isFullWidth = isFilesOrChangesRoute || isChecksRoute
  const responsiveWrapperClasses = isFullWidth ? fullWidthResponsiveWrapperClasses : condensedResponsiveWrapperClasses
  const stickyHeaderVariant = isFullWidth ? 'full-width' : 'condensed'

  return (
    <DockedPanelProvider isOnSupportedRoute={isFilesOrChangesRoute} pullRequestId={pullRequest.id}>
      <PageDataContextProvider basePageDataUrl={urls.conversation}>
        <UserNameDisplayContextProvider value={repository.userNameDisplayConfiguration || undefined}>
          <DiffComparisonViewer.Container
            className={`tmp-mt-4 ${responsiveWrapperClasses}`}
            dockedPanelWidth={viewSettings.dockedPanelWidth}
            owner={repository.ownerLogin}
            repo={repository.name}
            pullNumber={pullRequest.number}
          >
            <DiffComparisonViewer.Header divider="none" padding="none">
              <LivePullRequestHeader
                aliveChannels={aliveChannels}
                bannersData={bannersData}
                pageTitle={pageTitle}
                isFilesRoute={isFilesOrChangesRoute}
                stickyHeaderVariant={stickyHeaderVariant}
                repository={repository}
                pullRequest={pullRequest}
                stack={stack}
                urls={urls}
                user={user}
                mergeStatusButtonData={mergeStatusButtonData}
                viewSettings={viewSettings}
              />
            </DiffComparisonViewer.Header>
            {children}
          </DiffComparisonViewer.Container>
        </UserNameDisplayContextProvider>
      </PageDataContextProvider>
    </DockedPanelProvider>
  )
}
